import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button,Checkbox,Dropdown,Input } from 'components/atoms/index';
import { Modal } from '@/components/organisms/Modal/Modal';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import { SERVICE_TERMS_TEXT, PRIVACY_POLICY_TEXT } from '@/constants/agreements';
import { academyRegistrationApi } from '@/services/api';

const REJECTED_PREFILL_STORAGE_KEY = 'academy_rejected_prefill';
const PREFILL_TTL_MS = 10 * 60 * 1000;

type RejectedPrefillPayload = {
    loginId?: string;
    academyName?: string;
    adminName?: string;
    email?: string;
    phone?: string;
    seatCount?: number;
    businessRegistrationNumber?: string;
    reason?: string;
    issuedAt?: number;
};

const seatRangeFromCount = (count?: number | null): string | null => {
    if (count == null) return null;
    const value = Number(count);
    if (Number.isNaN(value) || value <= 0) return null;
    if (value <= 25) return '1-25';
    if (value <= 50) return '25-50';
    if (value <= 75) return '50-75';
    if (value <= 100) return '75-100';
    if (value <= 125) return '100-125';
    if (value <= 150) return '125-150';
    if (value <= 175) return '150-175';
    if (value <= 200) return '175-200';
    return '200+';
};

export default function AcademySignUp() {
    const [rejectMessage, setRejectMessage] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false); // 외부 메시지 수신 시에만 표시
    const [emailOtpStatus, setEmailOtpStatus] = useState<'idle'|'sent'|'verified'|'failed'>('idle');
    const [brnStatus, setBrnStatus] = useState<'idle'|'valid'|'invalid'>('idle');
    const [seatRange, setSeatRange] = useState<string | null>(null);
    const [emailInput, setEmailInput] = useState('');
    const [lastVerifiedEmail, setLastVerifiedEmail] = useState<string | null>(null);
    const [agreePrivacy, setAgreePrivacy] = useState(false);
    const [agreeTos, setAgreeTos] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordConfirmInput, setPasswordConfirmInput] = useState('');
    const passwordMismatch = passwordConfirmInput.length > 0 && passwordInput !== passwordConfirmInput;
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
    
    // 반려시 사유 출력 모달
    useEffect(() => {
        if (showRejectModal && rejectMessage && rejectMessage.trim() !== '') {
            Modal.alert({
                title: '반려사유',
                message: rejectMessage,
                okText: '확인'
            });
        }
    }, [showRejectModal, rejectMessage]);

    // 쿼리스트링(reason|rejectMessage)로 반려 사유 전달 시 자동 표시
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const reason = params.get('rejectMessage') || params.get('reason') || '';
        if (reason) {
            setRejectMessage(reason);
            setShowRejectModal(true);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const raw = sessionStorage.getItem(REJECTED_PREFILL_STORAGE_KEY);
        if (!raw) return;
        sessionStorage.removeItem(REJECTED_PREFILL_STORAGE_KEY);
        try {
            const parsed: RejectedPrefillPayload = JSON.parse(raw);
            if (parsed?.issuedAt && Date.now() - parsed.issuedAt > PREFILL_TTL_MS) {
                return;
            }
            const setValueByPlaceholder = (placeholder: string, value?: string | null) => {
                if (!value) return;
                const input = document.querySelector(`input[placeholder='${placeholder}']`) as HTMLInputElement | null;
                if (input) {
                    input.value = value;
                }
            };
            setValueByPlaceholder('아이디를 입력해주세요.', parsed.loginId);
            setValueByPlaceholder('학원명을 입력해주세요.', parsed.academyName);
            setValueByPlaceholder('대표자명을 입력해주세요.', parsed.adminName);
            setValueByPlaceholder('대표자 연락처를 입력해주세요.', parsed.phone);
            if (parsed.email) {
                setEmailInput(parsed.email);
            }
            if (parsed.businessRegistrationNumber) {
                const numberInputs = document.querySelectorAll("input[placeholder='인증번호를 입력해주세요.']");
                if (numberInputs.length > 1) {
                    const brnInput = numberInputs[numberInputs.length - 1] as HTMLInputElement;
                    brnInput.value = parsed.businessRegistrationNumber;
                }
            }
            const mappedSeatRange = seatRangeFromCount(parsed.seatCount);
            if (mappedSeatRange) {
                setSeatRange(mappedSeatRange);
            }
            if (parsed.reason) {
                setRejectMessage(parsed.reason);
                setShowRejectModal(true);
            }
        } catch {
            // ignore malformed stored data
        }
    }, []);

    // 가입신청 완료시 모달
    const handleSignUpComplete = async () => {
        // UI는 변경하지 않고, 단순히 현재 입력값들을 수집해 API 전송
        try {
            // 간단히 querySelector로 현재 값 읽기 (퍼블 구조 유지)
            const inputs = document.querySelectorAll('input');
            const values: Record<string, string> = {};
            inputs.forEach((el: any) => {
                if (el?.placeholder) {
                    values[el.placeholder] = el.value || '';
                }
            });

            // 필드 매핑 (placeholder에 기반한 매핑)
            // 중복 placeholder 처리: 첫번째 인증번호 = 이메일 OTP, 마지막 인증번호 = 사업자등록번호
            const otpInputs = Array.from(document.querySelectorAll("input[placeholder='인증번호를 입력해주세요.']")) as HTMLInputElement[];
            const emailOtpVal = otpInputs.length > 0 ? otpInputs[0].value || '' : '';
            const brnVal = otpInputs.length > 1 ? otpInputs[otpInputs.length - 1].value || '' : '';

            const loginId = (values['아이디를 입력해주세요.'] || '').trim();
            const password = (values['비밀번호를 입력해주세요.'] || '').trim();
            const academyName = (values['학원명을 입력해주세요.'] || '').trim();
            const adminName = (values['대표자명을 입력해주세요.'] || '').trim();
            const phone = (values['대표자 연락처를 입력해주세요.'] || '').trim();
            const email = (emailInput || '').trim();

            // 벨리데이션
            if (!loginId || loginId.length < 3) { await Modal.alert({ message: '아이디를 3자 이상 입력해주세요.', okText: '확인' }); return; }
            if (!password || !/^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/.test(password)) { await Modal.alert({ message: '비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상 입력해주세요.', okText: '확인' }); return; }
            if (passwordMismatch) { return; }
            if (!academyName) { await Modal.alert({ message: '학원명을 입력해주세요.', okText: '확인' }); return; }
            if (!adminName) { await Modal.alert({ message: '대표자명을 입력해주세요.', okText: '확인' }); return; }
            if (!phone) { await Modal.alert({ message: '대표자 연락처를 입력해주세요.', okText: '확인' }); return; }
            const phoneDigits = phone.replace(/[^0-9]/g, '');
            if (phoneDigits.length < 9) { await Modal.alert({ message: '대표자 연락처를 정확히 입력해주세요.', okText: '확인' }); return; }
            if (phone.length > 30) { await Modal.alert({ message: '대표자 연락처는 30자 이내로 입력해주세요.', okText: '확인' }); return; }
            if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { await Modal.alert({ message: '올바른 이메일을 입력해주세요.', okText: '확인' }); return; }
            if (!agreePrivacy || !agreeTos) { await Modal.alert({ message: '개인정보 처리방침과 서비스 이용약관에 모두 동의해주세요.', okText: '확인' }); return; }
            if (!captchaToken) { await Modal.alert({ message: '캡챠 인증을 완료해주세요.', okText: '확인' }); return; }
            if (!seatRange) { await Modal.alert({ message: '사용 인원을 선택해주세요.', okText: '확인' }); return; }

            // 추가 요구사항: 이메일/사업자 인증 모두 통과해야 가입 가능
            if (emailOtpStatus !== 'verified' || !lastVerifiedEmail || lastVerifiedEmail !== email) {
                await Modal.alert({ message: '현재 입력한 이메일 주소로 인증을 다시 진행해주세요.', okText: '확인' });
                return;
            }
            if (brnStatus !== 'valid') {
                await Modal.alert({ message: '사업자 등록번호 인증을 완료해주세요.', okText: '확인' });
                return;
            }

            // seatCount 산출 (상한값 사용, 200+는 200으로 처리)
            const seatCount = seatRange === '200+' ? 200 : (seatRange.includes('-') ? parseInt(seatRange.split('-')[1], 10) : undefined);

            const payload = {
                loginId,
                password,
                academyName,
                adminName,
                email,
                phone,
                businessRegistrationNumber: brnVal,
                seatCount,
                agreePrivacy,
                agreeTos,
                captchaToken: captchaToken || undefined,
                emailOtp: emailOtpVal || undefined,
            };

            await academyRegistrationApi.signUp(payload);

            await Modal.alert({
                message: '가입 신청이 완료되었습니다.\n알파영어 승인 후, 서비스 개설비용을 납부하여야 정상 접속 가능합니다.',
                okText: '확인'
            });
            window.location.href = '/academy/login';
        } catch (e: any) {
            await Modal.alert({
                title: '오류',
                message: e?.response?.data?.message || '가입 신청 중 오류가 발생했습니다.',
                okText: '확인'
            });
        }
    };

  return (
    <>
    <main className='flex flex-1 justify-center w-full bg-white md:py-10 md:items-center md:bg-transparent'>
        <div className='w-full max-w-[500px]'>
            <h1 className='pt-8 pb-5 heading1'>학원 가입 신청</h1>
            <div className='flex flex-col gap-5'>
                <div>
                    <p className='pb-1.5'>아이디</p>
                    <div className='flex gap-2 items-center'>
                    <Input
                        placeholder='아이디를 입력해주세요.'
                        maxLength={60}
                        />
                    <Button className='flex-none' onClick={async () => {
                        const el = document.querySelector("input[placeholder='아이디를 입력해주세요.']") as HTMLInputElement | null;
                        const loginId = el?.value?.trim() || '';
                        if (!loginId) {
                            await Modal.alert({ message: '아이디를 입력해주세요.', okText: '확인' });
                            return;
                        }
                        try {
                            const res = await academyRegistrationApi.checkLoginId(loginId);
                            // 서버는 { success, data: boolean(사용가능 여부), message } 형태로 응답
                            const ok = res?.data === true || res === true;
                            const msg = res?.message || (ok ? '사용 가능한 아이디입니다.' : '이미 사용중인 아이디입니다.');
                            await Modal.alert({ message: msg, okText: '확인' });
                        } catch (e: any) {
                            await Modal.alert({ title: '오류', message: '중복확인 중 오류가 발생했습니다.', okText: '확인' });
                        }
                    }}>중복확인</Button>
                    </div>
                </div>
                <Input
                label='비밀번호'
                placeholder='비밀번호를 입력해주세요.'
                type='password'
                maxLength={50}
                value={passwordInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPasswordInput(e.target.value)}
                />

                <div className='flex flex-col gap-1'>
                <Input
                label='비밀번호 확인'
                placeholder='비밀번호를 다시 입력해주세요.'
                type='password'
                maxLength={50}
                    value={passwordConfirmInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPasswordConfirmInput(e.target.value)}
                />
                    {passwordMismatch && (
                        <small className='text-xs text-red-500'>비밀번호가 일치하지 않습니다.</small>
                    )}
                </div>

                <Input
                label='학원명'
                placeholder='학원명을 입력해주세요.'
                maxLength={100}
                />

            <Input
                label='대표자명'
                placeholder='대표자명을 입력해주세요.'
                maxLength={100}
                />

            <Input
                label='대표자 연락처'
                placeholder='대표자 연락처를 입력해주세요.'
                type='tel'
                maxLength={30}
                inputMode='numeric'
                pattern='[0-9]*'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const digits = e.target.value.replace(/\\D/g, '');
                    e.target.value = digits;
                }}
                />

                <div>
                    <p className='pb-1.5'>대표자 이메일</p>
                    <div className='flex gap-2 items-center'>
                    <Input
                        placeholder='대표자 이메일을 입력해주세요.'
                        maxLength={120}
                        value={emailInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = e.target.value;
                            setEmailInput(next);
                            if (lastVerifiedEmail && lastVerifiedEmail !== next.trim()) {
                                setEmailOtpStatus('idle');
                                setLastVerifiedEmail(null);
                            }
                        }}
                        />
                    <Button className='flex-none' onClick={async (e:any) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        const email = emailInput.trim();
                        if (!email) { await Modal.alert({ message: '이메일을 입력해주세요.', okText: '확인' }); return; }
                        try {
                            await academyRegistrationApi.sendEmailOtp(email);
                            await Modal.alert({ message: '인증번호를 발송했습니다.', okText: '확인' });
                            setEmailOtpStatus('sent');
                            setLastVerifiedEmail(null);
                        } catch (e: any) {
                            const msg = e?.response?.data?.message || '인증번호 발송 중 오류가 발생했습니다.';
                            await Modal.alert({ title: '오류', message: msg, okText: '확인' });
                            setEmailOtpStatus('failed');
                            setLastVerifiedEmail(null);
                        }
                    }}>인증발송</Button>
                    </div>
                </div>

                <div>
                    <p className='pb-1.5'>인증번호 </p>
                    <div className='flex gap-2 items-start'>
                        <div className='flex flex-col gap-1 w-full'>
                        <Input
                            placeholder='인증번호를 입력해주세요.'
                            maxLength={6}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const digits = e.target.value.replace(/\\D/g, '');
                                e.target.value = digits;
                            }}
                            />
                            <small className='text-xs text-gray-500'>
                              {emailOtpStatus === 'sent' ? '인증번호가 발송되었습니다.' : emailOtpStatus === 'verified' ? '인증되었습니다.' : ''}
                            </small>
                        </div>
                    <Button className='flex-none' onClick={async (e:any) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        const container = (e?.currentTarget as HTMLElement)?.parentElement as HTMLElement | null; // 현재 섹션 범위로 제한
                        const codeEl = container?.querySelector("input[placeholder='인증번호를 입력해주세요.']") as HTMLInputElement | null;
                        const email = emailInput.trim();
                        const code = codeEl?.value?.trim() || '';
                        if (!email || !code) { await Modal.alert({ message: '이메일과 인증번호를 입력해주세요.', okText: '확인' }); return; }
                        try {
                            const res = await academyRegistrationApi.verifyEmailOtp(email, code);
                            const ok = res?.data === true || res === true;
                            await Modal.alert({ message: ok ? '인증되었습니다.' : '인증 실패', okText: '확인' });
                            setEmailOtpStatus(ok ? 'verified' : 'failed');
                            setLastVerifiedEmail(ok ? email : null);
                        } catch (e: any) {
                            const msg = e?.response?.data?.message || '인증 확인 중 오류가 발생했습니다.';
                            await Modal.alert({ title: '오류', message: msg, okText: '확인' });
                            setEmailOtpStatus('failed');
                            setLastVerifiedEmail(null);
                        }
                    }}>인증확인</Button>
                    </div>
                </div>

                <div>
                    <p className='pb-1.5'>사업자 등록 번호</p>
                    <div className='flex gap-2 items-start'>
                        <div className='flex flex-col gap-1 w-full'>
                        <Input
                            placeholder='인증번호를 입력해주세요.'
                            maxLength={30}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const digits = e.target.value.replace(/\\D/g, '');
                                e.target.value = digits;
                            }}
                            />
                            <small className='text-xs text-gray-500'>
                              {brnStatus === 'valid' ? '유효한 사업자 등록번호입니다.' : brnStatus === 'invalid' ? '유효하지 않은 사업자 등록번호입니다.' : ''}
                            </small>
                        </div>
                    <Button className='flex-none' onClick={async (e:any) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        const container = (e?.currentTarget as HTMLElement)?.parentElement as HTMLElement | null; // BRN 섹션 범위로 제한
                        const el = container?.querySelector("input[placeholder='인증번호를 입력해주세요.']") as HTMLInputElement | null;
                        const brn = el?.value?.trim() || '';
                        if (!brn) { await Modal.alert({ message: '사업자 등록번호를 입력해주세요.', okText: '확인' }); return; }
                        try {
                            const res: any = await academyRegistrationApi.verifyBrn(brn);
                            const ok = res?.data === true || res === true;
                            const msg = res?.message || (ok ? '유효한 사업자 등록번호입니다.' : '유효하지 않은 사업자 등록번호입니다.');
                            await Modal.alert({ message: msg, okText: '확인' });
                            setBrnStatus(ok ? 'valid' : 'invalid');
                        } catch (e: any) {
                            const msg = e?.response?.data?.message || '사업자 등록번호 확인 중 오류가 발생했습니다.';
                            await Modal.alert({ title: '오류', message: msg, okText: '확인' });
                        }
                    }}>인증확인</Button>
                    </div>
                </div>
                <div>
                    <p className='pb-1.5'>사용 인원</p>
                    <Dropdown
                    placeholder='사용 인원을 선택해주세요.'
                    options={[
                      { value: '1-25', label: '1 ~ 25명' },
                      { value: '25-50', label: '25 ~ 50명' },
                      { value: '50-75', label: '50 ~ 75명' },
                      { value: '75-100', label: '75 ~ 100명' },
                      { value: '100-125', label: '100 ~ 125명' },
                      { value: '125-150', label: '125 ~ 150명' },
                      { value: '150-175', label: '150 ~ 175명' },
                      { value: '175-200', label: '175 ~ 200명' },
                      { value: '200+', label: '200+명' },
                    ]}
                    value={seatRange ?? null}
                    onChange={(v) => setSeatRange(v)}
                    />

                </div>

            </div>
            <div className='flex flex-col gap-2 mt-5'>
                <div className='flex items-center justify-between gap-3'>
                <Checkbox
                label='개인정보 수집 및 이용 동의'
                    checked={agreePrivacy}
                    onChange={setAgreePrivacy}
                showIcon={false}
                />
                    <button
                        type='button'
                        className='text-xs text-gray-500 underline'
                        onClick={() => setShowPrivacyModal(true)}
                    >
                        내용보기
                    </button>
                </div>

                <div className='flex items-center justify-between gap-3'>
                <Checkbox
                label='서비스 이용약관'
                    checked={agreeTos}
                    onChange={setAgreeTos}
                showIcon={false}
                />
                    <button
                        type='button'
                        className='text-xs text-gray-500 underline'
                        onClick={() => setShowServiceModal(true)}
                    >
                        내용보기
                    </button>
                </div>
            </div>
            <div className='pt-5'>
                {recaptchaSiteKey ? (
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        onChange={(token: string | null) => setCaptchaToken(token || null)}
                        onExpired={() => setCaptchaToken(null)}
                    />
                ) : (
                    <p className='text-sm text-red-500'>캡챠 키가 설정되지 않았습니다. 관리자에게 문의해주세요.</p>
                )}
            </div>
            <Button className='mt-5 w-full' onClick={handleSignUpComplete}>가입완료</Button>
        </div>
    </main>
    <Dialog
        open={showPrivacyModal}
        onOk={() => setShowPrivacyModal(false)}
        onCancel={() => setShowPrivacyModal(false)}
        okText="닫기"
        className="max-w-3xl"
    >
        <div className='p-5 space-y-4 max-h-[60vh] overflow-auto'>
            <h3 className='text-xl font-bold text-gray-900'>개인정보 수집 및 이용 동의</h3>
            <pre className='whitespace-pre-wrap text-sm text-gray-700'>
{PRIVACY_POLICY_TEXT}
            </pre>
        </div>
    </Dialog>
    <Dialog
        open={showServiceModal}
        onOk={() => setShowServiceModal(false)}
        onCancel={() => setShowServiceModal(false)}
        okText="닫기"
        className="max-w-3xl"
    >
        <div className='p-5 space-y-4 max-h-[60vh] overflow-auto'>
            <h3 className='text-xl font-bold text-gray-900'>서비스 이용약관</h3>
            <pre className='whitespace-pre-wrap text-sm text-gray-700'>
{SERVICE_TERMS_TEXT}
            </pre>
        </div>
    </Dialog>
    </>
  )
}